import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { useDashboardInsights, type DashboardInsight } from '../../hooks/useDashboardInsights';
import { 
  ArrowLeft, 
  AlertTriangle, 
  TrendingUp, 
  Target, 
  CheckCircle,
  Clock,
  BarChart3,
  Users,
  Smartphone,
  Search,
  ExternalLink,
  Lightbulb
} from 'lucide-react';

const severityConfig = {
  critical: {
    color: 'bg-red-500',
    textColor: 'text-red-700 dark:text-red-300',
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    borderColor: 'border-red-200 dark:border-red-700/50',
    icon: AlertTriangle
  },
  high: {
    color: 'bg-orange-500',
    textColor: 'text-orange-700 dark:text-orange-300',
    bgColor: 'bg-orange-50 dark:bg-orange-900/20',
    borderColor: 'border-orange-200 dark:border-orange-700/50',
    icon: TrendingUp
  },
  medium: {
    color: 'bg-yellow-500',
    textColor: 'text-yellow-700 dark:text-yellow-300',
    bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
    borderColor: 'border-yellow-200 dark:border-yellow-700/50',
    icon: Target
  },
  low: {
    color: 'bg-blue-500',
    textColor: 'text-blue-700 dark:text-blue-300',
    bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    borderColor: 'border-blue-200 dark:border-blue-700/50',
    icon: CheckCircle
  }
};

const actionTypeConfig = {
  bounce_rate: {
    title: 'Bounce Rate Optimization',
    icon: BarChart3,
    category: 'Performance'
  },
  mobile_optimization: {
    title: 'Mobile Experience',
    icon: Smartphone,
    category: 'User Experience'
  },
  seo_strategy: {
    title: 'SEO Strategy',
    icon: Search,
    category: 'Marketing'
  },
  conversion_optimization: {
    title: 'Conversion Optimization',
    icon: Target,
    category: 'Revenue'
  }
};

export function InsightDetailPage() {
  const { projectId, insightId } = useParams<{ projectId: string; insightId: string }>();
  const navigate = useNavigate();
  const { insights, isLoading, error } = useDashboardInsights(projectId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading insight details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 text-red-500 dark:text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Failed to Load Insight</h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">There was an error loading this insight.</p>
        <Button onClick={() => navigate(`/projects/${projectId}/dashboard`)}>
          Back to Dashboard
        </Button>
      </div>
    );
  }

  const insight = insights.find((i: DashboardInsight) => i.id === insightId);

  if (!insight) {
    return (
      <div className="text-center py-12">
        <Target className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Insight Not Found</h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">The insight you're looking for doesn't exist or may have been updated.</p>
        <Button onClick={() => navigate(`/projects/${projectId}/dashboard`)}>
          Back to Dashboard
        </Button>
      </div>
    );
  }

  const severityInfo = severityConfig[insight.severity];
  const actionInfo = actionTypeConfig[insight.actionType] || actionTypeConfig['conversion_optimization']; // Fallback
  const SeverityIcon = severityInfo.icon;
  const ActionIcon = actionInfo.icon;

  const getActionPlan = (actionType: string) => {
    switch (actionType || 'conversion_optimization') {
      case 'bounce_rate':
        return {
          steps: [
            'Analyze page load speed using Google PageSpeed Insights',
            'Optimize images and compress assets',
            'Review and improve above-the-fold content',
            'Ensure mobile responsiveness',
            'A/B test different headline and CTA variations',
            'Monitor bounce rate improvements over 2-4 weeks'
          ],
          resources: [
            { title: 'Google PageSpeed Insights', url: 'https://pagespeed.web.dev/' },
            { title: 'Core Web Vitals Guide', url: 'https://web.dev/vitals/' },
            { title: 'Bounce Rate Optimization', url: 'https://support.google.com/analytics/answer/1009409' }
          ]
        };
      case 'mobile_optimization':
        return {
          steps: [
            'Audit mobile user experience with device testing',
            'Implement responsive design improvements',
            'Optimize touch targets and button sizes',
            'Reduce mobile page load times',
            'Test mobile checkout/conversion flows',
            'Monitor mobile conversion rate improvements'
          ],
          resources: [
            { title: 'Mobile-Friendly Test', url: 'https://search.google.com/test/mobile-friendly' },
            { title: 'Mobile UX Best Practices', url: 'https://developers.google.com/web/fundamentals/design-and-ux/principles' },
            { title: 'Mobile Performance Guide', url: 'https://web.dev/mobile/' }
          ]
        };
      case 'seo_strategy':
        return {
          steps: [
            'Conduct keyword research for target topics',
            'Create high-quality, relevant content',
            'Optimize meta titles and descriptions',
            'Improve internal linking structure',
            'Build authoritative backlinks',
            'Track organic traffic and rankings'
          ],
          resources: [
            { title: 'Google Search Console', url: 'https://search.google.com/search-console' },
            { title: 'SEO Starter Guide', url: 'https://developers.google.com/search/docs/beginner/seo-starter-guide' },
            { title: 'Keyword Research Tools', url: 'https://ads.google.com/home/tools/keyword-planner/' }
          ]
        };
      case 'conversion_optimization':
        return {
          steps: [
            'Set up conversion tracking and goals',
            'Analyze conversion funnel drop-off points',
            'A/B test key conversion elements',
            'Optimize checkout/signup process',
            'Implement retargeting campaigns',
            'Monitor conversion rate improvements'
          ],
          resources: [
            { title: 'Google Analytics Goals', url: 'https://support.google.com/analytics/answer/1012040' },
            { title: 'Conversion Optimization Guide', url: 'https://support.google.com/google-ads/answer/2684489' },
            { title: 'A/B Testing Best Practices', url: 'https://support.google.com/optimize/answer/7405543' }
          ]
        };
      default:
        return {
          steps: ['Contact our support team for a customized action plan'],
          resources: []
        };
    }
  };

  const actionPlan = getActionPlan(insight.actionType);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/projects/${projectId}/dashboard`)}
            className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="h-6 w-px bg-gray-300 dark:bg-gray-600" />
          <div className="flex items-center space-x-2">
            <ActionIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <span className="text-sm text-gray-600 dark:text-gray-400">{actionInfo.category}</span>
          </div>
        </div>
        <Badge variant="secondary" className="text-xs">
          {insight.dataSource?.period || 'Last 30 days'}
        </Badge>
      </div>

      {/* Insight Overview */}
      <Card className={`p-6 ${severityInfo.borderColor} border-l-4`}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className={`w-10 h-10 ${severityInfo.bgColor} rounded-lg flex items-center justify-center`}>
              <SeverityIcon className={`w-5 h-5 ${severityInfo.textColor}`} />
            </div>
            <div>
              <div className="flex items-center space-x-2 mb-1">
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">{insight.title}</h1>
                <Badge 
                  variant="secondary" 
                  className={`${severityInfo.color} text-white text-xs`}
                >
                  {insight.severity.toUpperCase()}
                </Badge>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">{actionInfo.title}</p>
            </div>
          </div>
        </div>

        <p className="text-gray-700 dark:text-gray-300 mb-4">{insight.description}</p>

        {/* Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className={`p-3 ${severityInfo.bgColor} rounded-lg`}>
            <p className="text-sm text-gray-600 dark:text-gray-400">Current Value</p>
            <p className={`text-lg font-semibold ${severityInfo.textColor}`}>
              {insight.dataSource?.currentValue || 'N/A'}
              {insight.dataSource?.metric?.includes('rate') ? '%' : ''}
            </p>
          </div>
          <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
            <p className="text-sm text-gray-600 dark:text-gray-400">Industry Benchmark</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white">
              {insight.dataSource?.benchmarkValue || 'N/A'}
              {insight.dataSource?.metric?.includes('rate') ? '%' : ''}
            </p>
          </div>
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <p className="text-sm text-gray-600 dark:text-gray-400">Difference</p>
            <p className="text-lg font-semibold text-blue-700 dark:text-blue-300">
              {insight.dataSource?.difference !== undefined 
                ? `${insight.dataSource.difference > 0 ? '+' : ''}${insight.dataSource.difference}` 
                : 'N/A'}
              {insight.dataSource?.metric?.includes('rate') ? '%' : ''}
            </p>
          </div>
        </div>

        <div className="border-t dark:border-gray-700 pt-4">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Business Impact</h3>
          <p className="text-gray-700 dark:text-gray-300">{insight.businessValue || insight.impact || 'Improved website performance and user experience'}</p>
        </div>
      </Card>

      {/* Recommendation */}
      <Card className="p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Lightbulb className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">AI Recommendation</h2>
        </div>
        <p className="text-gray-700 dark:text-gray-300 mb-4">{insight.recommendation || insight.description}</p>
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700/50 rounded-lg p-4">
          <p className="text-sm text-blue-800 dark:text-blue-300">
            <strong>Expected Impact:</strong> {insight.impact}
          </p>
        </div>
      </Card>

      {/* Action Plan */}
      <Card className="p-6">
        <div className="flex items-center space-x-2 mb-4">
          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Step-by-Step Action Plan</h2>
        </div>
        
        <div className="space-y-3">
          {actionPlan.steps.map((step, index) => (
            <div key={index} className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center text-sm font-medium mt-0.5">
                {index + 1}
              </div>
              <p className="text-gray-700 dark:text-gray-300 flex-1">{step}</p>
            </div>
          ))}
        </div>

        {actionPlan.resources.length > 0 && (
          <div className="mt-6 pt-6 border-t dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Helpful Resources</h3>
            <div className="space-y-2">
              {actionPlan.resources.map((resource, index) => (
                <a
                  key={index}
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span className="text-sm">{resource.title}</span>
                </a>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Timeline */}
      <Card className="p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Clock className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Implementation Timeline</h2>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center space-x-4">
            <div className="w-3 h-3 bg-blue-500 dark:bg-blue-400 rounded-full"></div>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Week 1-2: Setup & Analysis</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Implement tracking and gather baseline data</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="w-3 h-3 bg-yellow-500 dark:bg-yellow-400 rounded-full"></div>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Week 3-4: Implementation</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Execute optimization changes and monitor</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="w-3 h-3 bg-green-500 dark:bg-green-400 rounded-full"></div>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Week 5-6: Optimization</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Fine-tune and measure results</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Actions */}
      <div className="flex items-center space-x-4">
        <Button 
          onClick={() => navigate(`/agents/data`)}
          className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 dark:from-blue-500 dark:to-purple-500 dark:hover:from-blue-600 dark:hover:to-purple-600"
        >
          <Users className="w-4 h-4 mr-2" />
          Ask AI Agent for Help
        </Button>
        <Button 
          variant="outline"
          onClick={() => navigate(`/projects/${projectId}/analytics`)}
        >
          <BarChart3 className="w-4 h-4 mr-2" />
          View Analytics
        </Button>
      </div>
    </div>
  );
} 